import React, { useState, useEffect } from 'react';
import { Box, Chip } from '@mui/material';
import { SingleSelect } from '../single-select';
import { AssistantType } from './assistant-type';
import { moduleClientFactory } from '../../api-service/module-client-factory';
import { useFeatureToggles } from '../../feature-toggles';
import { useIsAllowed } from '../../authentication/use-is-allowed';
import { ResourceOperations, ResourceTypes } from '../../authentication/resources';

export const draftsToggle = 'AiSummaStandardQuestionDrafts';

const danishTextCollator = new Intl.Collator('da', { sensitivity: 'base' });

function compareAlphabetically(left: string, right: string) {
  return danishTextCollator.compare(left, right);
}

// A copy keeps its original's name, so the draft is listed right after the question it came from.
function compareByTitleThenDraft(left: IStandardQuestion, right: IStandardQuestion) {
  return compareAlphabetically(left.title, right.title) || Number(left.isDraft) - Number(right.isDraft);
}

// The prefix is what a screen reader reads and what typing "kladde" filters on.
function getOptionLabel(option?: IStandardQuestion) {
  if (option?.isDraft) {
    return `Kladde: ${option.title}`;
  }

  return option?.title ?? '';
}

export interface IStandardQuestion {
  id: string;
  title: string;
  question: string;
  topic: string;
  isSelectable: boolean;
  isDraft: boolean;
}

export interface IChatStandardQuestionSelectProps {
  assistantType: AssistantType;
  onStandardQuestionChange: (question: string, standardQuestionId?: string, isDraft?: boolean) => void;
  isLoading?: boolean;
  // Chosen in Summa's settings; the list only follows it.
  showDrafts?: boolean;
}

export function ChatStandardQuestionSelect(props: IChatStandardQuestionSelectProps) {
  const { assistantType, onStandardQuestionChange, isLoading, showDrafts } = props;
  const [standardQuestion, setStandardQuestion] = useState<IStandardQuestion>();
  const [standardQuestions, setStandardQuestions] = useState<IStandardQuestion[]>([]);
  const client = moduleClientFactory.createAiSearchClient();

  const { isFeatureEnabled } = useFeatureToggles();
  const mayWriteStandardQuestions = useIsAllowed([{ resourceType: ResourceTypes.BusinessConfiguration, resourceOperation: ResourceOperations.Write }]);
  const draftsEnabled = isFeatureEnabled(draftsToggle);
  const draftsAvailable = draftsEnabled && mayWriteStandardQuestions && assistantType === AssistantType.Citizen;
  const draftsShown = draftsAvailable && !!showDrafts;

  async function fetchAiStandardQuestions() {
    const result = await client.getAiStandardQuestions({
      aiStandardQuestionArea: assistantType as number,
    });

    setStandardQuestions(
      result.standardQuestions?.map(q => ({
        id: q.id,
        title: q.title ?? '',
        question: q.question ?? '',
        topic: q.topic ?? '',
        isSelectable: true,
        isDraft: q.isDraft ?? false,
      })) ?? []
    );
  }

  useEffect(() => {
    fetchAiStandardQuestions();
  }, []);

  // A superuser may have changed a draft in the administration since the list was fetched.
  function handleOpen() {
    fetchAiStandardQuestions().catch(keepTheListAlreadyShown);
  }

  function keepTheListAlreadyShown() {
    return undefined;
  }

  function isShown(question: IStandardQuestion) {
    return !question.isDraft || draftsShown;
  }

  function getSelectOptions(): IStandardQuestion[] {
    const shownQuestions = standardQuestions.filter(isShown);
    const uniqueTopics = Array.from(new Set(shownQuestions.map(q => q.topic))).sort(compareAlphabetically);

    return uniqueTopics.flatMap(topic => {
      const topicHeader: IStandardQuestion = {
        id: `topic:${topic}`,
        title: topic,
        question: '',
        topic: '',
        isSelectable: false,
        isDraft: false,
      };

      const topicQuestions = shownQuestions.filter(q => q.topic === topic).sort(compareByTitleThenDraft);

      return [topicHeader, ...topicQuestions];
    });
  }

  function isSameOption(option?: IStandardQuestion, value?: IStandardQuestion) {
    return draftsEnabled ? option?.id === value?.id : option?.title === value?.title;
  }

  const handleStandardQuestionChange = (value?: IStandardQuestion) => {
    // While trying drafts the same question is asked again and again, so the field is left empty to allow picking it once more.
    setStandardQuestion(draftsShown ? undefined : value);
    onStandardQuestionChange(value?.question ?? '', value?.id, value?.isDraft);
  };

  function getPaddingForOption(option?: IStandardQuestion): number {
    if (option?.isSelectable) {
      return 5;
    }
    return 0;
  }

  return assistantType === AssistantType.Citizen || assistantType === AssistantType.Fasit ? (
    <Box sx={{ display: 'flex', ml: 2, mb: 2, alignItems: 'center' }}>
      <Box style={{ width: '100%' }}>
        <SingleSelect<IStandardQuestion | undefined>
          label="Standardspørgsmål"
          onChange={handleStandardQuestionChange}
          onOpen={draftsAvailable ? handleOpen : undefined}
          value={standardQuestion}
          getOptionDisabled={opt => !opt?.isSelectable}
          options={getSelectOptions()}
          isOptionEqualToValue={isSameOption}
          getOptionLabel={getOptionLabel}
          renderOption={(prop: object, option?: IStandardQuestion) => (
            <li
              {...prop}
              key={option?.id}
              role={option?.isSelectable ? 'option' : 'presentation'}
              aria-label={getOptionLabel(option)}
              style={{
                fontWeight: option?.isSelectable ? undefined : 'bolder',
                fontSize: option?.isSelectable ? undefined : '.9rem',
                opacity: 1,
              }}
            >
              <span style={{ paddingLeft: getPaddingForOption(option) }}>
                {option?.isDraft && <Chip label="Kladde" size="small" sx={{ mr: 1 }} />}
                {option?.title}
              </span>
            </li>
          )}
          readOnly={isLoading}
          variant="outlined"
        />
      </Box>
    </Box>
  ) : (
    <div />
  );
}
